/**
 * Storezone — Seller Layout
 *
 * Dense top-bar layout for the Seller / POS interface.
 * Designed for fast operational use at a point-of-sale station.
 *
 * Structure:
 *   Topbar: [Store] [Branch] [Quick nav] [User]
 *   Content: full-height workspace
 */

import { type ReactNode, useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { Icons } from '@/lib/icons'
import { BranchSelector } from '@/components/BranchSelector'
import { NotificationBell } from '@/components/NotificationBell'
import { LoadingPage } from '@/components/states/LoadingPage'
import { apiClient } from '@/lib/api'
import type { BranchResponse } from '@/types/branches'
import './SellerLayout.css'

interface SellerLayoutProps {
  children: ReactNode
}

const NAV_ITEMS = [
  { to: '/pos',           label: 'POS',       Icon: Icons.Products,  end: true },
  { to: '/pos/products',  label: 'Products',  Icon: Icons.Inventory, end: false },
  { to: '/pos/customers', label: 'Customers', Icon: Icons.Users,     end: false },
  { to: '/pos/orders',    label: 'Orders',    Icon: Icons.Orders,    end: false },
  { to: '/pos/dashboard', label: 'Dashboard', Icon: Icons.Dashboard, end: false },
]

export function SellerLayout({ children }: SellerLayoutProps) {
  const { profile, signOut } = useAuth()
  const { activeBranchId, branding, ready, tenantId } = useTenant()
  const navigate = useNavigate()
  const [activeBranchName, setActiveBranchName] = useState<string | null>(null)

  // Fetch active branch name so we can display it in the header
  useEffect(() => {
    if (!activeBranchId || !tenantId) return
    apiClient.get<BranchResponse[]>('/api/v1/tenants/branches')
      .then(branches => {
        const found = branches.find(b => b.id === activeBranchId)
        if (found) setActiveBranchName(found.name)
      })
      .catch(() => {})
  }, [activeBranchId, tenantId])

  if (!ready) {
    return <LoadingPage message="Loading workspace..." />
  }

  async function handleSignOut(e?: any) {
    if (e) e.preventDefault()
    await signOut()
    navigate('/auth/login', { replace: true })
  }

  const themeStyles = {
    '--sz-primary':   branding?.primary_color   || undefined,
    '--sz-secondary': branding?.secondary_color || undefined,
    '--sz-accent':    branding?.accent_color    || undefined,
    '--sz-sidebar':   branding?.sidebar_color   || undefined,
    '--sz-radius':    branding?.button_radius   ? `${branding.button_radius}px` : undefined,
  } as React.CSSProperties

  return (
    <div className="sz-seller" style={themeStyles}>

      {/* Topbar */}
      <header className="sz-seller__topbar">

        {/* Store + Branch info */}
        <div className="sz-seller__store">
          {branding?.logo_url ? (
            <img src={branding.logo_url as string} alt="Logo" className="sz-seller__store-mark" style={{ objectFit: 'contain', background: 'transparent' }} />
          ) : (
            <span className="sz-seller__store-mark">SZ</span>
          )}
          <div className="sz-seller__store-info">
            {activeBranchName && (
              <span className="sz-seller__branch-id" style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: '1rem' }}>
                <Icons.Branches size={16} />
                {activeBranchName}
              </span>
            )}
          </div>
        </div>

        {/* Quick nav */}
        <nav className="sz-seller__nav" aria-label="Seller navigation">
          {NAV_ITEMS.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `sz-seller__nav-link${isActive ? ' sz-seller__nav-link--active' : ''}`
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sz-space-4)' }}>
          <BranchSelector />
          <NotificationBell />

          <div className="sz-seller__user">
            <span className="sz-seller__user-name truncate">
              {profile?.full_name ?? profile?.email ?? '—'}
            </span>
            <button
              type="button"
              id="seller-signout"
              className="sz-seller__signout"
              onClick={handleSignOut}
              title="Sign out"
              aria-label="Sign out"
            >
              <Icons.LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="sz-seller__main">
        {children}
      </main>
    </div>
  )
}
